import type { SxProps, Theme } from '@mui/material/styles';
import palette from '@src/app/themes/palette';
import {
  WARNING_COLORS,
  type WarningItem,
  type WarningLevel,
} from '@src/shared/lib/warnings';
import { WarningIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import Tooltip from '@src/shared/ui/Tooltip';
import type { ReactElement, ReactNode } from 'react';

export type WarningIndicatorVariant = 'table-row' | 'input';

const WARNING_BACKGROUNDS: Record<WarningLevel, string> = {
  red: palette.red['100'],
  amber: palette.orange['400'],
};

// Tooltip content for an indicator that stands for several warnings.
export function WarningMessageList({
  warnings,
}: {
  warnings: readonly WarningItem[];
}) {
  return (
    <Box
      component="ul"
      sx={{ listStyle: 'none', m: 0, pl: 0, textAlign: 'left' }}
    >
      {warnings.map((warning) => (
        <Box component="li" key={warning.code}>
          {warning.message}
        </Box>
      ))}
    </Box>
  );
}

type WarningTooltipProps = {
  children: ReactElement;
  message?: ReactNode;
  warning: WarningLevel;
};

// The popover of a warning icon: the icon again and the message, in the
// colours of the warning level. Wraps any element that stands for a warning.
export function WarningTooltip({
  children,
  message,
  warning,
}: WarningTooltipProps) {
  const color = WARNING_COLORS[warning];
  const backgroundColor = WARNING_BACKGROUNDS[warning];

  return (
    <Tooltip
      title={
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            textAlign: 'center',
          }}
        >
          <WarningIcon
            color="inherit"
            sx={{ color, display: 'block', fontSize: 18 }}
          />
          {message}
        </Box>
      }
      slotProps={{
        arrow: {
          sx: {
            color: backgroundColor,
            '&::before': {
              border: `1px solid ${color}`,
            },
          },
        },
        tooltip: {
          sx: {
            backgroundColor,
            border: `1px solid ${color}`,
            boxShadow: 'none',
            color,
            px: 2,
            py: 1.25,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
}

type WarningIndicatorBodyProps = {
  ariaLabel?: string;
  edge?: boolean;
  message?: ReactNode;
  sx?: SxProps<Theme>;
  variant: WarningIndicatorVariant;
  warning: WarningLevel;
};

function WarningIndicatorBody({
  ariaLabel,
  edge = true,
  message,
  sx,
  variant,
  warning,
}: WarningIndicatorBodyProps) {
  const color = WARNING_COLORS[warning];

  return (
    <Box
      sx={[
        {
          alignItems: 'center',
          bgcolor: variant === 'table-row' ? '#e9eef3' : 'transparent',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          width: variant === 'table-row' ? 40 : 20,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <WarningTooltip message={message} warning={warning}>
        <Box
          aria-label={
            ariaLabel ?? (typeof message === 'string' ? message : undefined)
          }
          role="img"
          tabIndex={0}
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: 20,
            justifyContent: 'center',
            width: 20,
          }}
        >
          <WarningIcon
            color="inherit"
            sx={{ color, display: 'block', fontSize: 18 }}
          />
        </Box>
      </WarningTooltip>
      {variant === 'table-row' && edge ? (
        <Box
          aria-hidden
          sx={{
            bgcolor: color,
            bottom: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            width: 2,
          }}
        />
      ) : null}
    </Box>
  );
}

type InlineWarningIndicatorProps = {
  ariaLabel?: string;
  // Vertical centre of the `input` variant, in px from the wrapper top.
  centerY?: number;
  message?: ReactNode;
  variant?: WarningIndicatorVariant;
  warning: WarningLevel | null;
};

const INPUT_INDICATOR_GAP = 11.5;

// Sits in the gutter left of its nearest positioned ancestor and scrolls with
// it natively, so that ancestor must be `position: relative`.
export function InlineWarningIndicator({
  ariaLabel,
  centerY,
  message,
  variant = 'table-row',
  warning,
}: InlineWarningIndicatorProps) {
  if (!warning) {
    return null;
  }

  return (
    <WarningIndicatorBody
      ariaLabel={ariaLabel}
      message={message}
      variant={variant}
      warning={warning}
      sx={
        variant === 'table-row'
          ? { bottom: 0, position: 'absolute', right: '100%', top: 0 }
          : {
              height: 20,
              position: 'absolute',
              right: `calc(100% + ${INPUT_INDICATOR_GAP}px)`,
              top: centerY ?? '50%',
              transform: 'translateY(-50%)',
            }
      }
    />
  );
}

type GutterWarningIndicatorProps = {
  ariaLabel?: string;
  edge?: boolean;
  message?: ReactNode;
  warning: WarningLevel | null;
};

// Fills a DataGrid gutter cell, which scrolls and clips with the grid rows.
export function GutterWarningIndicator({
  ariaLabel,
  edge,
  message,
  warning,
}: GutterWarningIndicatorProps) {
  if (!warning) {
    return null;
  }

  return (
    <WarningIndicatorBody
      ariaLabel={ariaLabel}
      edge={edge}
      message={message}
      variant="table-row"
      warning={warning}
      sx={{ height: '100%' }}
    />
  );
}
